import React, { useEffect, useState } from "react";
import { ChevronDown, Check, DollarSign } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";

const flagUrl = (code) => `https://flagcdn.com/w40/${code}.png`;

export default function CurrencySwitcher({ transparent = false, menuAlign = "right" }) {
  const { currency, setCurrency, currencies } = useCurrency();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!e.target.closest("[data-testid='currency-switcher']")) setOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [open]);

  const baseColor = transparent
    ? "text-white hover:text-marigold border-white/30"
    : "text-foreground hover:text-saffron border-cream-300";

  return (
    <div className="relative" data-testid="currency-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="currency-switcher-btn"
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs uppercase tracking-wider border rounded-sm transition-colors ${baseColor}`}
        aria-label="Change currency"
      >
        <DollarSign className="w-3.5 h-3.5" />
        <span>{currency.code}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className={`absolute top-full mt-2 w-56 max-w-[calc(100vw-2rem)] bg-white border border-cream-200 rounded-sm shadow-lg z-50 overflow-hidden animate-fade-in ${
            menuAlign === "left" ? "left-0" : "right-0"
          }`}
          data-testid="currency-switcher-menu"
        >
          <div className="px-4 py-3 border-b border-cream-100 text-[10px] uppercase tracking-[0.2em] text-saffron font-semibold">
            Display currency
          </div>
          <ul className="max-h-80 overflow-y-auto">
            {currencies.map((c) => (
              <li key={c.code}>
                <button
                  type="button"
                  onClick={() => {
                    setCurrency(c);
                    setOpen(false);
                  }}
                  data-testid={`currency-option-${c.code}`}
                  className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-cream-100 transition-colors ${
                    currency.code === c.code ? "text-saffron font-semibold bg-cream-50" : "text-foreground"
                  }`}
                >
                  <img
                    src={flagUrl(c.flag)}
                    alt={c.code}
                    className="w-6 h-4 object-cover rounded-[2px] flex-shrink-0"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <span className="font-mono text-xs text-foreground/70 w-9">{c.code}</span>
                  <span className="flex-1 text-xs">{c.name}</span>
                  {currency.code === c.code && <Check className="w-4 h-4 text-saffron" />}
                </button>
              </li>
            ))}
          </ul>
          <div className="px-4 py-2 border-t border-cream-100 text-[10px] text-foreground/55">
            Prices auto-convert from USD
          </div>
        </div>
      )}
    </div>
  );
}
